import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './NewRequestScreen.module.scss';
import { useToasts } from 'react-toast-notifications';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Col, Row } from 'react-flexbox-grid';
import { FaAngleDown, FaAngleRight, FaCalendarAlt, FaInfoCircle } from 'react-icons/fa';
import IconBack from 'assets/svg/Arrow-grey.svg';
import FormItem from 'common/components/formItem/FormItem';
import Label from 'common/components/label/Label';
import InputGroup from 'common/components/inputGroup/InputGroup';
import Button from 'common/components/button/Button';
import SelectController from 'common/components/select/SelectController';
import InputResizableController from 'common/components/input/InputResizableController';
import DateWithHourController from 'common/components/dateWithHour/DateWithHourController';
import InputError from 'common/components/inputError/InputError';
import Input from 'common/components/input/Input';
import InputResizable from 'common/components/input/InputResizable';
import AvatarWithText from 'common/components/avatar/AvatarWithText';
import DataTable from 'common/components/dataTable/DataTable';
import { DataTableColumn, ExpandableRowConfig } from 'common/components/dataTable/models';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import { DATE_FORMAT_DEFAUT, DefaultAssetImage, DEFAULT_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE, TIME_FORMAT_DEFAULT, removeAccents } from 'Config';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import DriversService from 'api/drivers/DriversService';
import LocalsService from 'api/locals/LocalsService';
import VehicleBrandsService from 'api/vehicleBrands/VehicleBrandsService';
import VehicleModelsService from 'api/vehicleModels/VehicleModelsService';
import VehicleSegmentsService from 'api/vehicleSegments/VehicleSegmentsService';
import VehicleReservationsService from 'api/vehicleReservations/VehicleReservationsService';
import { VehicleReservationsSearchCriteria } from 'api/vehicleReservations/models/VehicleReservationsSearchCriteria';
import { VehicleReservationViewModel } from 'api/vehicleReservations/models/VehicleReservationViewModel';
import { VehicleReservationDto } from 'api/vehicleReservations/models/VehicleReservationDto';
import { ReservationsVehicleListItemDto } from 'api/vehicles/models/ReservationsVehicleListItemDto';
import { CatalogModelsSearchCriteria } from 'api/catalog/models/CatalogModelsSearchCriteria';

import DateFormat from 'common/components/dateFormat/dateFormat';
import moment from 'moment';
import Badge from 'common/components/badge/Badge';
import { getReservationStatusBadgePreset, getReservationStatusLabel } from 'common/utils/reservationStatus';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import ReactTooltip from 'react-tooltip';
import ReservationHistoryTab from '../historyTab/ReservationHistoryTab';
import ReservationTripsSection from '../tripsSection/ReservationTripsSection';

const itemsPerPage = DEFAULT_PAGINATION_ITEMS_PER_PAGE;

interface RequestForm {
    startDate?: Date;
    endDate?: Date;
    driverId?: string;
    localId?: string;
    reason?: string;
}

interface VehicleSearchFilters {
    vehicleBrandId?: string | null;
    vehicleModelId?: string | null;
    vehicleSegmentId?: string | null;
    vehicleRegistrationNumber?: string | null;
    reservationStartDate?: Date | null;
    reservationEndDate?: Date | null;
    localId?: string | null;
}

interface NewRequestScreenProps {
    isRequestMode?: boolean;
    isDetailsMode?: boolean;
}

const NewRequestScreen: React.FC<NewRequestScreenProps> = ({ isRequestMode = false, isDetailsMode = false }) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();
    const history = useHistory();
    const { itemId } = useParams<{ itemId: string }>();
    const location = useLocation();
    const editParam = new URLSearchParams(location.search).get('edit') === 'true';
    const imageCacheKey = useRef(new Date().getTime());

    // --- User permissions ---
    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const canWrite = UsersService.hasPolicies(userProfile?.policies ?? [], ['SETTINGUP_RESERVATIONS_WRITE'], 'OR');
    const canApprove = UsersService.hasPolicies(userProfile?.policies ?? [], ['SETTINGUP_RESERVATIONS_APPROVE'], 'OR');
    const canEditAll = UsersService.hasPolicies(userProfile?.policies ?? [], ['SETTINGUP_RESERVATIONS_EDIT_ALL'], 'OR');

    // --- Company reservation type ---
    const companyReservationType = userProfile?.companyReservationType ?? 'DIRECT';

    // Vehicle selection is required for:
    // - DIRECT type (always)
    // - WORKFLOW_WITH_ASSET (always)
    // - Approver creating new request (auto-approved)
    // NOT required for WORKFLOW_WITHOUT_ASSET requests (regular users)
    const isNewRequestByApprover = !isDetailsMode && isRequestMode && canApprove;
    const requiresVehicleSelection =
        !isRequestMode || // reservation mode always requires vehicle
        companyReservationType === 'DIRECT' ||
        companyReservationType === 'WORKFLOW_WITH_ASSET' ||
        isNewRequestByApprover;

    // --- Details mode state ---
    const [reservation, setReservation] = useState<VehicleReservationDto | null>(null);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const [isEditing, setIsEditing] = useState(editParam);
    const [dateValidationError, setDateValidationError] = useState<string | null>(null);
    const [hasBeenEdited, setHasBeenEdited] = useState(false);
    const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
    const dateValidationTimeout = useRef<NodeJS.Timeout | null>(null);
    const [showCancelApprovalModal, setShowCancelApprovalModal] = useState(false);

    // --- Create mode state ---
    const [selectedVehicle, setSelectedVehicle] = useState<ReservationsVehicleListItemDto | null>(null);

    const isOwner = reservation ? reservation.userCreatedId === userProfile?.id : true;
    const isPendingApproval = reservation?.status === 'PENDING_APPROVAL';
    const isApproved = reservation?.status === 'APPROVED';
    const isActive = reservation?.status === 'ACTIVE';

    // --- Permission logic (matching mobile) ---
    const canEditRequest = !isDetailsMode || (isPendingApproval && (canEditAll || isOwner)) || (isApproved && (canEditAll || isOwner)) || (isActive && canWrite && (canEditAll || isOwner));
    const hasWorkflow = companyReservationType !== 'DIRECT';
    const isAutoApproved = reservation?.status === 'APPROVED' && reservation?.approvedByUserId === reservation?.userCreatedId;
    const canDeleteFromReservationsTab = !isRequestMode && (hasWorkflow ? (canEditAll || canApprove) : (canEditAll || (canWrite && isOwner)));
    const canDeleteRequest = isDetailsMode && ((isPendingApproval && isOwner) || (isActive && canWrite && (canEditAll || isOwner)) || (isAutoApproved && isOwner) || canDeleteFromReservationsTab);

    // --- Approver mode: form is directly editable for approvers on pending requests (matching mobile) ---
    const isApproverOnPending = isDetailsMode && canApprove && isPendingApproval;
    const isFormEditable = !isDetailsMode || isEditing || isApproverOnPending;

    // --- Back URL ---
    const fromCalendar = new URLSearchParams(location.search).get('from') === 'calendars';
    const backUrl = fromCalendar ? '/reservations' : isRequestMode ? '/reservations?tab=requests' : '/reservations?tab=reservations';
    const backUrlWithRefresh = fromCalendar ? '/reservations?refresh=true' : isRequestMode ? '/reservations?tab=requests&refresh=true' : '/reservations?tab=reservations&refresh=true';

    // --- Request form ---
    const requestForm = useForm<RequestForm>({ shouldUnregister: false });
    const { errors, handleSubmit, watch } = requestForm;
    const watchedStartDate = watch('startDate');
    const watchedEndDate = watch('endDate');
    const watchedLocalId = watch('localId');

    const [driversOptions, setDriversOptions] = useState<SelectValueLabel[]>([]);
    const [localsOptions, setLocalsOptions] = useState<SelectValueLabel[]>([]);

    // --- Vehicle search filters ---
    const searchForm = useForm<VehicleSearchFilters>({ shouldUnregister: false });
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [brandsOptions, setBrandsOptions] = useState<SelectValueLabel[]>([]);
    const [modelsOptions, setModelsOptions] = useState<SelectValueLabel[]>([]);
    const [segmentsOptions, setSegmentsOptions] = useState<SelectValueLabel[]>([]);
    const [searchLocalsOptions, setSearchLocalsOptions] = useState<SelectValueLabel[]>([]);

    // --- Vehicle list ---
    const [vehicles, setVehicles] = useState<ReservationsVehicleListItemDto[]>([]);
    const [totalItems, setTotalItems] = useState(0);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [criteria, setCriteria] = useState<any>({
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
    });
    const [openRows, setOpenRows] = useState<string[]>([]);

    // --- Init ---
    useEffect(() => {
        const loadData = async () => {
            try {
                Loading.show();
                const [drivers, locals, brands, segments, searchLocals] = await Promise.all([
                    DriversService.catalog(),
                    LocalsService.catalog(),
                    VehicleBrandsService.catalog(),
                    VehicleSegmentsService.catalog(),
                    LocalsService.catalog(),
                ]);
                setDriversOptions(drivers);
                setLocalsOptions(locals);
                setBrandsOptions(brands);
                setSegmentsOptions(segments);
                setSearchLocalsOptions(searchLocals);

                // Auto-select logged-in user as driver if creating new reservation and user is in drivers list
                if (!isDetailsMode && userProfile?.id) {
                    const userInDrivers = drivers.find(d => d.value === userProfile.id);
                    if (userInDrivers) {
                        requestForm.setValue('driverId', userProfile.id);
                    }
                }

                // Load reservation data in details mode
                if (isDetailsMode && itemId) {
                    const reservationData = await VehicleReservationsService.getById(itemId);
                    setReservation(reservationData);

                    // Populate form with existing data (shouldDirty ensures watch subscribers re-render)
                    requestForm.setValue('startDate', reservationData.startDate ? new Date(reservationData.startDate) : undefined, { shouldDirty: true });
                    requestForm.setValue('endDate', reservationData.endDate ? new Date(reservationData.endDate) : undefined, { shouldDirty: true });
                    requestForm.setValue('driverId', reservationData.driverId);
                    requestForm.setValue('localId', reservationData.localId);
                    requestForm.setValue('reason', reservationData.reason);

                    // Set the selected vehicle from the reservation data so the UI shows correct button labels
                    const existingVehicleId = reservationData.vehicleId || reservationData.requestedVehicleId;
                    if (existingVehicleId) {
                        setSelectedVehicle({
                            id: existingVehicleId,
                            vehicleRegistrationNumber: reservationData.vehicleRegistrationNumber || reservationData.requestedVehicleRegistrationNumber || '',
                            vehicleBrandName: reservationData.vehicleBrandName || reservationData.requestedVehicleBrandName || '',
                            vehicleModelName: reservationData.vehicleModelName || reservationData.requestedVehicleModelName || '',
                            photoUrl: reservationData.vehiclePhotoUrl || reservationData.requestedVehiclePhotoUrl || '',
                            availability: true,
                            availabilities: [],
                            immobilized: reservationData.immobilized,
                        });
                    }

                    // Sync is handled automatically by the useEffect watching requestForm dates/localId
                }

                Loading.hide();
            } catch (error) {
                Loading.hide();
                Logger.error(LOGGER_LOG_TYPE.VEHICLES, `Couldn't load form data`, error);
                addToast(t('common.messages.error_load_info'), { appearance: 'error' });
            }
        };
        loadData();
    }, [isDetailsMode, itemId]);

    // --- Vehicle list loading ---
    const getVehiclesData = async (isLoadMore = false) => {
        try {
            if (!isLoadMore) {
                Loading.show();
            } else {
                setIsLoadingMore(true);
            }

            const result = await VehicleReservationsService.getVehicles(criteria);
            setTotalItems(result.totalItems);

            if (isLoadMore) {
                setVehicles(prev => [...prev, ...result.items]);
            } else {
                setVehicles(result.items);
            }

            Loading.hide();
            setIsLoadingMore(false);
        } catch (error) {
            Loading.hide();
            setIsLoadingMore(false);
            Logger.error(LOGGER_LOG_TYPE.VEHICLES, `Couldn't get vehicles list`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        }
    };

    useEffect(() => {
        getVehiclesData(criteria.page > 1);
    }, [criteria]);

    // Sync request form dates/local to search form and auto-trigger search
    useEffect(() => {
        searchForm.setValue('reservationStartDate', watchedStartDate || null);
        searchForm.setValue('reservationEndDate', watchedEndDate || null);
        searchForm.setValue('localId', watchedLocalId || null);

        if (watchedStartDate && watchedEndDate) {
            applySearchFilters(searchForm.getValues());
        }
    }, [watchedStartDate, watchedEndDate, watchedLocalId]);

    const loadMore = useCallback(() => {
        if (isLoadingMore || vehicles.length >= totalItems) return;
        setCriteria((prev: any) => ({ ...prev, page: prev.page + 1 }));
    }, [isLoadingMore, vehicles.length, totalItems]);

    // --- Search filters logic ---
    const getModelByBrand = async () => {
        try {
            const modelsList = await VehicleModelsService.catalog({ brandId: searchForm.getValues('vehicleBrandId') } as CatalogModelsSearchCriteria);
            setModelsOptions(modelsList);
            if (!searchForm.getValues('vehicleModelId') && modelsList.length === 1) {
                searchForm.setValue('vehicleModelId', modelsList.find(x => x.value)?.value);
            }
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.VEHICLES, `Couldn't get models`, error);
        }
    };


    const applySearchFilters = (f: VehicleSearchFilters) => {
        setVehicles([]);
        setOpenRows([]);
        setTotalItems(0);
        setCriteria({
            itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
            page: 1,
            brandId: f.vehicleBrandId,
            modelId: f.vehicleModelId,
            segmentId: f.vehicleSegmentId,
            registrationNumber: f.vehicleRegistrationNumber,
            startDate: f.reservationStartDate,
            endDate: f.reservationEndDate,
            ...(isDetailsMode && itemId ? { id: itemId } : {}),
        });
    };

    const clearSearchFilters = async () => {
        // Keep synced fields (dates, local) from request form, only clear search-specific fields
        searchForm.reset({
            vehicleBrandId: null,
            vehicleModelId: null,
            vehicleSegmentId: null,
            vehicleRegistrationNumber: null,
            reservationStartDate: watchedStartDate || null,
            reservationEndDate: watchedEndDate || null,
            localId: watchedLocalId || null,
        });
        const [brands, segments] = await Promise.all([
            VehicleBrandsService.catalog(),
            VehicleSegmentsService.catalog(),
        ]);
        setBrandsOptions(brands);
        setModelsOptions([]);
        setSegmentsOptions(segments);
        applySearchFilters(searchForm.getValues());
    };

    // --- Expandable rows ---
    const getReservations = async (vehicle: ReservationsVehicleListItemDto, page: number) => {
        if (!vehicle.id) return;
        Loading.show();
        try {
            const reservationsCriteria = {
                itemsPerPage,
                page,
                orderBy: 'desc',
                orderColumn: 'vr.start_date',
                vehicleId: vehicle.id,
            };
            const reservationsList = await VehicleReservationsService.getListByVehicleId(reservationsCriteria);
            setVehicles(vv => vv.map(v => {
                if (v.id === vehicle.id) {
                    const reservations = page > 1 ? (v.reservations || []) : [];
                    return {
                        ...v,
                        reservations: [...reservations, ...reservationsList.items],
                        reservationsReachedEnd: reservationsList.totalPages === 0 || reservationsList.currentPage === reservationsList.totalPages,
                    };
                }
                return v;
            }));
            Loading.hide();
        } catch (error) {
            setVehicles(vv => vv.map(v => {
                if (v.id === vehicle.id) {
                    return { ...v, reservations: v.reservations || [], reservationsReachedEnd: true };
                }
                return v;
            }));
            Logger.error(LOGGER_LOG_TYPE.VEHICLES, `Couldn't get reservations`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
            Loading.hide();
        }
    };

    const toggleRowOpen = (row: ReservationsVehicleListItemDto, forceOpen = false) => {
        if (!row.id) return;
        if (openRows.find(id => id === row.id) && !forceOpen) {
            setOpenRows(openRows.filter(id => id !== row.id));
        } else {
            if (row.reservationsReachedEnd === undefined || forceOpen) {
                getReservations(row, 1);
            }
            setOpenRows([...openRows, row.id]);
        }
    };

    const hasBlockingPeriods = (row: ReservationsVehicleListItemDto) =>
        (row.maintenancePeriods && row.maintenancePeriods.length > 0)
        || (row.reservationPeriods && row.reservationPeriods.length > 0);

    const isRowExpanded = (row: ReservationsVehicleListItemDto) => Boolean(openRows.find(id => id === row.id));

    const onSubRowsLoadMore = (row: ReservationsVehicleListItemDto) => {
        const totalPages = Math.ceil((row.reservations || []).length / itemsPerPage);
        getReservations(row, totalPages + 1);
    };

    const canSubRowsLoadMore = (row: ReservationsVehicleListItemDto): boolean => !row.reservationsReachedEnd;

    // --- Immobilized check helper ---
    // selectedVehicle may have been built from a reservation DTO and thus lack the `immobilized` flag.
    // Fall back to the availability list fetched from the backend so we still detect it.
    const isSelectedVehicleImmobilized = () => {
        if (!selectedVehicle?.id) return false;
        if (selectedVehicle.immobilized) return true;
        const match = vehicles.find(v => v.id === selectedVehicle.id);
        return !!match?.immobilized;
    };

    // --- Select vehicle (just store selection) ---
    const onSelectVehicle = (vehicle: ReservationsVehicleListItemDto) => {
        if (vehicle.immobilized) {
            addToast(t('reservations.vehicle_immobilized_warning'), { appearance: 'error' });
            return;
        }
        setSelectedVehicle(vehicle);
        // Re-validate dates with new vehicle
        const startDate = requestForm.getValues('startDate');
        const endDate = requestForm.getValues('endDate');
        if (startDate && endDate) {
            validateDates(new Date(startDate), new Date(endDate), vehicle.id);
        }
    };

    // --- Save reservation/request ---
    const onSave = async (formValues: RequestForm) => {
        // Validate date range
        if (formValues.startDate && formValues.endDate && new Date(formValues.startDate).getTime() >= new Date(formValues.endDate).getTime()) {
            setDateValidationError(t('reservations.invalid_date_range') as string);
            addToast(t('reservations.invalid_date_range'), { appearance: 'warning' });
            return;
        }

        // Check if there's a date validation error
        if (dateValidationError) {
            addToast(dateValidationError, { appearance: 'warning' });
            return;
        }

        // Check if vehicle is required but not selected
        if (requiresVehicleSelection && !selectedVehicle) {
            addToast(t('requests.vehicle_required'), { appearance: 'warning' });
            return;
        }

        if (isSelectedVehicleImmobilized()) {
            addToast(t('reservations.vehicle_immobilized_warning'), { appearance: 'error' });
            return;
        }

        try {
            Loading.show();
            await VehicleReservationsService.create({
                startDate: formValues.startDate!,
                endDate: formValues.endDate!,
                driverId: formValues.driverId,
                vehicleId: selectedVehicle?.id,
                vehicleRegistrationNumber: selectedVehicle?.vehicleRegistrationNumber || '',
                reason: formValues.reason,
                localId: formValues.localId,
            });
            Loading.hide();
            addToast(t('common.messages.record_save_success'), { appearance: 'success' });
            history.push(backUrlWithRefresh);
        } catch (error) {
            Loading.hide();
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't create reservation/request`, error);
            addToast(t('common.messages.record_save_error'), { appearance: 'error' });
        }
    };

    // --- Approve request (matching mobile: validate form → update with changes → backend auto-approves) ---
    const onApprove = async () => {
        if (!reservation?.id) return;

        // Validate form
        const isValid = await new Promise<boolean>(resolve => {
            handleSubmit(
                () => resolve(true),
                () => resolve(false),
            )();
        });

        if (!isValid) return;

        // Check vehicle selection
        const vehicleId = selectedVehicle?.id || reservation.vehicleId;
        if (!vehicleId) {
            addToast(t('requests.vehicle_required_approve'), { appearance: 'warning' });
            return;
        }

        if (isSelectedVehicleImmobilized()) {
            addToast(t('reservations.vehicle_immobilized_warning'), { appearance: 'error' });
            return;
        }

        // Check date validation error
        if (dateValidationError) {
            addToast(dateValidationError, { appearance: 'warning' });
            return;
        }

        try {
            Loading.show();
            const formValues = requestForm.getValues();
            // Update with form changes + vehicle - backend auto-approves when user has canApprove permission
            await VehicleReservationsService.update({
                ...reservation,
                startDate: formValues.startDate!,
                endDate: formValues.endDate!,
                driverId: formValues.driverId,
                localId: formValues.localId,
                reason: formValues.reason,
                vehicleId: vehicleId,
            });
            Loading.hide();
            addToast(t('common.messages.record_save_success'), { appearance: 'success' });
            history.push(backUrlWithRefresh);
        } catch (error) {
            Loading.hide();
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't approve request`, error);
            addToast(t('common.messages.record_save_error'), { appearance: 'error' });
        }
    };

    // --- Reject request ---
    const onReject = async () => {
        if (!reservation?.id) return;

        if (!rejectionReason.trim()) {
            addToast(t('requests.rejection_reason_required'), { appearance: 'warning' });
            return;
        }

        try {
            Loading.show();
            await VehicleReservationsService.reject(reservation.id, rejectionReason);
            Loading.hide();
            setShowRejectModal(false);
            addToast(t('common.messages.record_save_success'), { appearance: 'success' });
            history.push(backUrlWithRefresh);
        } catch (error) {
            Loading.hide();
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't reject request`, error);
            addToast(t('common.messages.record_save_error'), { appearance: 'error' });
        }
    };

    // --- Delete request/reservation ---
    const onDelete = async () => {
        if (!reservation?.id) return;

        try {
            Loading.show();
            await VehicleReservationsService.remove(reservation);
            Loading.hide();
            setShowDeleteModal(false);
            addToast(t('common.messages.record_delete_success'), { appearance: 'success' });
            history.push(backUrlWithRefresh);
        } catch (error) {
            Loading.hide();
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't delete request/reservation`, error);
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
        }
    };

    // --- Update request/reservation ---
    const onUpdate = async (formValues: RequestForm) => {
        if (!reservation?.id) return;

        // Validate date range
        if (formValues.startDate && formValues.endDate && new Date(formValues.startDate).getTime() >= new Date(formValues.endDate).getTime()) {
            setDateValidationError(t('reservations.invalid_date_range') as string);
            addToast(t('reservations.invalid_date_range'), { appearance: 'warning' });
            return;
        }

        // Check if there's a date validation error
        if (dateValidationError) {
            addToast(dateValidationError, { appearance: 'warning' });
            return;
        }

        if (isSelectedVehicleImmobilized()) {
            addToast(t('reservations.vehicle_immobilized_warning'), { appearance: 'error' });
            return;
        }

        try {
            Loading.show();
            await VehicleReservationsService.update({
                ...reservation,
                startDate: formValues.startDate!,
                endDate: formValues.endDate!,
                driverId: formValues.driverId,
                localId: formValues.localId,
                reason: formValues.reason,
                vehicleId: selectedVehicle?.id || reservation.vehicleId,
            });
            Loading.hide();
            setIsEditing(false);
            setHasBeenEdited(true);
            setHistoryRefreshKey(prev => prev + 1);
            addToast(t('common.messages.record_save_success'), { appearance: 'success' });
            // Reload reservation data
            const updatedReservation = await VehicleReservationsService.getById(reservation.id);
            setReservation(updatedReservation);
        } catch (error) {
            Loading.hide();
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't update request/reservation`, error);
            addToast(t('common.messages.record_save_error'), { appearance: 'error' });
        }
    };

    // --- Cancel editing ---
    const onCancelEdit = () => {
        if (reservation) {
            // Reset form to original values (shouldDirty ensures watch subscribers re-render)
            requestForm.setValue('startDate', reservation.startDate ? new Date(reservation.startDate) : undefined, { shouldDirty: true });
            requestForm.setValue('endDate', reservation.endDate ? new Date(reservation.endDate) : undefined, { shouldDirty: true });
            requestForm.setValue('driverId', reservation.driverId);
            requestForm.setValue('localId', reservation.localId);
            requestForm.setValue('reason', reservation.reason);
            // Reset selected vehicle to original
            const existingVehicleId = reservation.vehicleId || reservation.requestedVehicleId;
            if (existingVehicleId) {
                setSelectedVehicle({
                    id: existingVehicleId,
                    vehicleRegistrationNumber: reservation.vehicleRegistrationNumber || reservation.requestedVehicleRegistrationNumber || '',
                    vehicleBrandName: reservation.vehicleBrandName || reservation.requestedVehicleBrandName || '',
                    vehicleModelName: reservation.vehicleModelName || reservation.requestedVehicleModelName || '',
                    photoUrl: reservation.vehiclePhotoUrl || reservation.requestedVehiclePhotoUrl || '',
                    availability: true,
                    availabilities: [],
                });
            }
        }
        setIsEditing(false);
    };

    // --- Validate dates in real-time ---
    const validateDates = useCallback((startDate?: Date, endDate?: Date, vehicleId?: string) => {
        // Clear previous timeout
        if (dateValidationTimeout.current) {
            clearTimeout(dateValidationTimeout.current);
        }

        // Clear error if dates are not both set
        if (!startDate || !endDate) {
            setDateValidationError(null);
            return;
        }

        // Validate date order first
        if (startDate.getTime() >= endDate.getTime()) {
            setDateValidationError(t('reservations.invalid_date_range') as string);
            return;
        }

        // Debounce the validation call
        dateValidationTimeout.current = setTimeout(async () => {
            try {
                const isValid = await VehicleReservationsService.validateDates({
                    startDate,
                    endDate,
                    vehicleId: vehicleId || selectedVehicle?.id,
                    id: reservation?.id, // Exclude current reservation when editing
                } as VehicleReservationsSearchCriteria);
                if (!isValid) {
                    setDateValidationError(t('requests.dates_conflict') as string);
                } else {
                    setDateValidationError(null);
                }
            } catch (error) {
                Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't validate dates`, error);
                // Don't show error on API failure, let submit handle it
            }
        }, 500);
    }, [selectedVehicle, reservation, t]);

    // --- Cancel approval (revert to pending) ---
    const onCancelApproval = async () => {
        if (!reservation?.id) return;

        try {
            Loading.show();
            await VehicleReservationsService.cancelApproval(reservation.id);
            Loading.hide();
            setShowCancelApprovalModal(false);
            addToast(t('common.messages.record_save_success'), { appearance: 'success' });
            history.push(backUrlWithRefresh);
        } catch (error) {
            Loading.hide();
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't cancel approval`, error);
            addToast(t('common.messages.record_save_error'), { appearance: 'error' });
        }
    };

    // --- Render cells ---
    const renderLocalNames = (row: ReservationsVehicleListItemDto) => {
        if (!row.localNames) return <></>;
        const allNames = row.localNames.split(', ').filter(n => n.trim().length > 0);
        if (allNames.length === 0) return <></>;

        const maxVisible = 3;
        const visible = allNames.slice(0, maxVisible);
        const remaining = allNames.slice(maxVisible);
        const tooltipId = `newreq_vehicle_agencies_${row.id}`;

        return (
            <div
                className={styles.localNamesCell}
                data-tip={t('reservations.list.agencies_label') as string}
                data-for={tooltipId}
            >
                {visible.map((name, i) => (
                    <span key={`newreq_agency_${row.id}_${i}`} className={styles.localName}>{name}</span>
                ))}
                {remaining.length > 0 && (
                    <div className={styles.localNamesOverflow}>
                        <span className={styles.localNamesMoreBadge}>+{remaining.length}</span>
                        <div className={styles.localNamesTooltip}>
                            {remaining.map((name, i) => (
                                <div key={`newreq_agency_extra_${row.id}_${i}`}>{name}</div>
                            ))}
                        </div>
                    </div>
                )}
                <ReactTooltip id={tooltipId} effect="solid" />
            </div>
        );
    };

    const renderVehicleCell = (row: ReservationsVehicleListItemDto) => (
        <div className={styles.vehicleCell}>
            <div className={styles.imageCell}>
                <div className={styles.contentImage} style={{ width: '5rem', height: '3rem' }}>
                    <div className={styles.img} style={{
                        width: '5rem', height: '3rem',
                        backgroundImage: row?.photoUrl
                            ? 'url(' + row.photoUrl + '?_=' + imageCacheKey.current + ')'
                            : row?.vehicleSegmentDefaultPhotoUrl
                                ? 'url(' + row.vehicleSegmentDefaultPhotoUrl + '?_=' + imageCacheKey.current + ')'
                                : 'url(' + DefaultAssetImage + ')'
                    }} />
                </div>
            </div>
            <div className={styles.description}>
                <div className={styles.bold}>{row.vehicleRegistrationNumber}</div>
                {(row.vehicleBrandName || row.vehicleModelName) && (
                    <div className={styles.brandModel}>
                        {row.vehicleBrandName}{row.vehicleBrandName && row.vehicleModelName ? ' | ' : ''}{row.vehicleModelName}
                    </div>
                )}
                {renderLocalNames(row)}
            </div>
        </div>
    );

    const renderDateCell = (row: VehicleReservationViewModel) => (
        <div>
            <div>
                {row.startDate ?
                    <span>
                        <span className={styles.bold}>{t('reservations.list.date_start')}:</span> <DateFormat value={row.startDate} format={DATE_FORMAT_DEFAUT} />
                        {' '}{new Date(row.startDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    : '-'}
            </div>
            <div>
                {row.endDate ?
                    <span>
                        <span className={styles.bold}>{t('reservations.list.date_end')}:</span> <DateFormat value={row.endDate} format={DATE_FORMAT_DEFAUT} />
                        {' '}{new Date(row.endDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    : '-'}
            </div>
        </div>
    );

    const renderDriverCell = (row: VehicleReservationViewModel) => (
        <div>
            <AvatarWithText src={row.driverPhotoUrl} size="x2" hideAvatar={!row.driverName}>
                <div className={styles.bold}>{row.driverName || '-'}</div>
            </AvatarWithText>
            {row.driverEmail && <div className={styles.driverEmail}>{row.driverEmail}</div>}
        </div>
    );

    const hasDatesFilter = Boolean(criteria.startDate && criteria.endDate);

    const renderAvailability = (row: ReservationsVehicleListItemDto) => (
        <div>
            {row.immobilized && (
                <Badge text={t('reservations.list.immobilized') as string} preset="danger" />
            )}
            {!row.immobilized && hasDatesFilter && (
                <>
                    {row.availability && (
                        <Badge text={t('reservations.list.available') as string} preset="greenAmerica" />
                    )}
                    {!row.availability && (
                        <>
                            <Badge text={t('reservations.list.not_available') as string} preset="danger" />
                            {row.availabilities && row.availabilities.length > 0 && (
                                <div data-tip={t('reservations.list.availability_in_defined_range')} data-for="availability-range-tooltip">
                                    {row.availabilities.map((a, i) => (
                                        <div key={i} style={{ marginTop: '0.25rem', fontSize: '0.7rem' }}>{
                                            a.endDate
                                                ? moment(a.endDate).diff(a.startDate, 'minute') <= 1 ? null
                                                    : moment(a.startDate).isSame(a.endDate, 'day')
                                                        ? t('reservations.list.available_at', { date: moment(new Date(a.startDate)).format(DATE_FORMAT_DEFAUT), hourFrom: moment(new Date(a.startDate)).format(TIME_FORMAT_DEFAULT), hourTo: moment(new Date(a.endDate)).format(TIME_FORMAT_DEFAULT) })
                                                        : t('reservations.list.available_between', { dateFrom: moment(new Date(a.startDate)).format(DATE_FORMAT_DEFAUT), hourFrom: moment(new Date(a.startDate)).format(TIME_FORMAT_DEFAULT), dateTo: moment(new Date(a.endDate)).format(DATE_FORMAT_DEFAUT), hourTo: moment(new Date(a.endDate)).format(TIME_FORMAT_DEFAULT) })
                                                : t('reservations.list.available_from', { date: moment(new Date(a.startDate)).format(DATE_FORMAT_DEFAUT + ' HH:mm') })
                                        }</div>
                                    ))}
                                    <ReactTooltip id="availability-range-tooltip" />
                                </div>
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    );

    // --- DataTable columns ---
    const vehicleColumns = useMemo<DataTableColumn<ReservationsVehicleListItemDto>[]>(() => [
        {
            field: 'vehicleRegistrationNumber' as keyof ReservationsVehicleListItemDto,
            name: <span className={styles.columnHeaderWithInfo}>
                {t('reservations.list.vehicle') as string}
                <FaInfoCircle
                    className={styles.infoIcon}
                    data-tip={t('reservations.only_show_vehicles_available_for_reservation')}
                    data-for="assets-tooltip"
                />
                <ReactTooltip id="assets-tooltip" />
            </span>,
            width: 300,
            hideOrderButton: true,
            cellStyle: { overflow: 'visible' },
            renderCell: row => renderVehicleCell(row),
        },
        {
            field: 'driverName' as keyof ReservationsVehicleListItemDto,
            name: t('reservations.list.driver') as string,
            width: 250,
            hideOrderButton: true,
            renderCell: () => <></>,
        },
        {
            field: 'reason' as keyof ReservationsVehicleListItemDto,
            name: t('reservations.destination_reason') as string,
            width: 200,
            hideOrderButton: true,
            renderCell: () => <></>,
        },
        {
            field: 'availability' as keyof ReservationsVehicleListItemDto,
            name: t('reservations.list.availability') as string,
            width: 300,
            hideOrderButton: true,
            renderCell: row => renderAvailability(row),
        },
    ], [t, hasDatesFilter]);

    // --- Expandable row config ---
    const renderWarningBanner = (vehicle: ReservationsVehicleListItemDto): ReactElement => (
        <div style={{
            backgroundColor: 'rgba(251, 191, 36, 0.12)',
            borderLeft: '3px solid #f59e0b',
            padding: '0.5rem 0.75rem',
            color: '#92400e',
            fontSize: '0.8rem',
            lineHeight: 1.4,
        }}>
            {vehicle.maintenancePeriods?.map((m, i) => (
                <div key={`mnt-${i}`}>
                    {t('reservations.list.unavailable_due_to_maintenance', {
                        dateFrom: moment(new Date(m.startDate)).format(DATE_FORMAT_DEFAUT),
                        hourFrom: moment(new Date(m.startDate)).format(TIME_FORMAT_DEFAULT),
                        dateTo: moment(new Date(m.endDate)).format(DATE_FORMAT_DEFAUT),
                        hourTo: moment(new Date(m.endDate)).format(TIME_FORMAT_DEFAULT),
                    })}
                </div>
            ))}
            {vehicle.reservationPeriods?.map((r, i) => (
                <div key={`res-${i}`}>
                    {t(r.driverName ? 'reservations.list.unavailable_due_to_reservation_with_driver' : 'reservations.list.unavailable_due_to_reservation', {
                        dateFrom: moment(new Date(r.startDate)).format(DATE_FORMAT_DEFAUT),
                        hourFrom: moment(new Date(r.startDate)).format(TIME_FORMAT_DEFAULT),
                        dateTo: moment(new Date(r.endDate)).format(DATE_FORMAT_DEFAUT),
                        hourTo: moment(new Date(r.endDate)).format(TIME_FORMAT_DEFAULT),
                        driver: r.driverName,
                    })}
                </div>
            ))}
        </div>
    );

    const expandableRowConfig: ExpandableRowConfig<ReservationsVehicleListItemDto> = {
        isExpanded: row => isRowExpanded(row),
        onToggle: row => toggleRowOpen(row),
        renderContent: () => <></>,
        renderExpandedCells: vehicle => {
            const rows: { key: string; className?: string; cells: Record<string, ReactElement | null>; colSpans?: Record<string, number>; fullContent?: ReactElement }[] = [];

            if (hasBlockingPeriods(vehicle)) {
                rows.push({
                    key: `warn-${vehicle.id}`,
                    cells: {},
                    fullContent: renderWarningBanner(vehicle),
                });
            }

            const reservations = vehicle.reservations || [];
            reservations.forEach((r, i) => {
                rows.push({
                    key: r.id || `res-${i}`,
                    cells: {
                        vehicleRegistrationNumber: renderDateCell(r),
                        driverName: renderDriverCell(r),
                        reason: <>{r.reason}</>,
                    } as Record<string, ReactElement | null>,
                });
            });

            return rows;
        },
        renderExpandedFooter: vehicle => {
            const reservations = vehicle.reservations || [];
            if (reservations.length === 0 && vehicle.reservationsReachedEnd !== undefined) {
                return <div className={styles.noResults}>{t('reservations.without_reservations')}</div>;
            }
            if (canSubRowsLoadMore(vehicle)) {
                return (
                    <div className={styles.loadMoreBtn} onClick={() => onSubRowsLoadMore(vehicle)}>
                        {t('common.load_more')}
                    </div>
                );
            }
            return null;
        },
    };

    const renderSelectAction = (row: ReservationsVehicleListItemDto) => {
        const isCurrentVehicle = selectedVehicle?.id === row.id;
        const isAvailable = row.availability;
        const isImmobilized = !!row.immobilized;

        // If this is the currently selected vehicle, show "Selecionado" badge (not clickable)
        if (isCurrentVehicle) {
            return (
                <div className={styles.selectedBadge}>
                    <span className={styles.selectedBadgeText}>{t('reservations.selected')}</span>
                </div>
            );
        }

        const buttonText = isDetailsMode && selectedVehicle ? t('reservations.change_asset') : t('reservations.select');

        return (
            <Button
                type="button"
                text={buttonText as string}
                size={'extraSmall'}
                className={styles.selectButton}
                isDisabled={!hasDatesFilter || !isAvailable || isImmobilized}
                onClick={e => { e?.stopPropagation(); onSelectVehicle(row); }}
            />
        );
    };

    // --- Get header title ---
    const getHeaderTitle = () => {
        if (isDetailsMode) {
            if (isRequestMode) {
                if (isApproverOnPending) return t('reservations.request_management' as any) as string;
                if (isEditing) return t('reservations.request_edit' as any) as string;
                return isPendingApproval ? t('reservations.request_management' as any) as string : t('reservations.request_details');
            }
            return t('reservations.reservation_details');
        }
        return isRequestMode ? t('reservations.new_request') : t('reservations.new_reservation');
    };

    // --- Get vehicle info for display ---
    const getVehicleInfo = () => {
        if (!reservation) return null;

        const vehicleId = reservation.vehicleId || reservation.requestedVehicleId;
        const regNumber = reservation.vehicleRegistrationNumber || reservation.requestedVehicleRegistrationNumber;
        const brandName = reservation.vehicleBrandName || reservation.requestedVehicleBrandName;
        const modelName = reservation.vehicleModelName || reservation.requestedVehicleModelName;
        const photoUrl = reservation.vehiclePhotoUrl || reservation.requestedVehiclePhotoUrl;

        if (!vehicleId && !regNumber) return null;

        return { vehicleId, regNumber, brandName, modelName, photoUrl };
    };

    const vehicleInfo = getVehicleInfo();

    return (
        <div className={styles.fullHeight}>
            {/* Header */}
            <div className={styles.header}>
                <h1 className={styles.title}>
                    <img src={IconBack} className={styles.icon} onClick={() => history.push(hasBeenEdited ? backUrlWithRefresh : backUrl)} alt="back" />
                    {getHeaderTitle()}
                </h1>
                {isDetailsMode && isRequestMode && reservation?.status && (
                    <Badge
                        text={getReservationStatusLabel(reservation.status, t)}
                        preset={getReservationStatusBadgePreset(reservation.status)}
                    />
                )}
            </div>

            {/* Content Area */}
            <div className={styles.contentArea}>
                {/* Warning banner when the currently assigned vehicle is immobilized */}
                {isSelectedVehicleImmobilized() && (
                    <div className={styles.rejectionReason}>
                        <div className={styles.rejectionReasonText}>
                            {t('reservations.vehicle_immobilized_warning')}
                        </div>
                    </div>
                )}

                {/* Rejection info for rejected requests */}
                {isDetailsMode && reservation?.status === 'REJECTED' && (
                    <div className={styles.rejectionReason}>
                        <div className={styles.rejectionApproverInfo}>
                            <span className={styles.rejectionApproverLabel}>{t('requests.rejected_by_label')}:</span>
                            {reservation.approverName ? ` ${reservation.approverName}` : ''}
                            {reservation.approvedDate && ` - ${moment(reservation.approvedDate).format(DATE_FORMAT_DEFAUT)}`}
                        </div>
                        {reservation.rejectionReason && (
                            <>
                                <div className={styles.rejectionTitle}>{t('requests.rejection_reason_title')}</div>
                                <div className={styles.rejectionReasonText}>{reservation.rejectionReason}</div>
                            </>
                        )}
                    </div>
                )}

                {/* Request form */}
                <form id="requestForm" className={styles.formSection} onSubmit={handleSubmit(isDetailsMode && isEditing ? onUpdate : onSave)}>
                    <Row>
                        <Col sm={12} md={4}>
                            <FormItem>
                                <Label className={styles.label} required={isFormEditable}>{t('reservations.list.date_start')}</Label>
                                <DateWithHourController
                                    form={requestForm}
                                    datePlaceholder={t('common.date')}
                                    dateFormat={DATE_FORMAT_DEFAUT}
                                    dateCustomInput={<InputGroup icon={<FaCalendarAlt />} removeIconOrTextBorderRadius={true} />}
                                    name="startDate"
                                    rules={{ required: isFormEditable ? t('common.errors.required') as string : false }}
                                    isDisabled={!isFormEditable}
                                    timePlaceholder={t('common.hour')}
                                    onChangeDate={(date: Date) => {
                                        const endDate = requestForm.getValues('endDate');
                                        if (date && endDate && new Date(date).getTime() >= new Date(endDate).getTime()) {
                                            requestForm.setValue('endDate', undefined);
                                            setDateValidationError(null);
                                        } else if (date && endDate) {
                                            validateDates(new Date(date), new Date(endDate));
                                        }
                                    }}
                                />
                                <InputError error={errors.startDate} />
                            </FormItem>
                        </Col>
                        <Col sm={12} md={4}>
                            <FormItem>
                                <Label className={styles.label} required={isFormEditable}>{t('reservations.list.date_end')}</Label>
                                <DateWithHourController
                                    form={requestForm}
                                    datePlaceholder={t('common.date')}
                                    dateFormat={DATE_FORMAT_DEFAUT}
                                    dateCustomInput={<InputGroup icon={<FaCalendarAlt />} removeIconOrTextBorderRadius={true} />}
                                    name="endDate"
                                    rules={{ required: isFormEditable ? t('common.errors.required') as string : false }}
                                    isDisabled={!isFormEditable}
                                    timePlaceholder={t('common.hour')}
                                    minDate={watchedStartDate || undefined}
                                    onChangeDate={(date: Date) => {
                                        const startDate = requestForm.getValues('startDate');
                                        if (date && startDate) {
                                            validateDates(new Date(startDate), new Date(date));
                                        }
                                    }}
                                />
                                <InputError error={errors.endDate} />
                                {dateValidationError && <InputError error={{ type: 'manual', message: dateValidationError }} />}
                            </FormItem>
                        </Col>
                        <Col sm={12} md={4}>
                            <FormItem>
                                <Label className={styles.label} required={isFormEditable}>{t('reservations.list.driver')}</Label>
                                <SelectController
                                    form={requestForm}
                                    name="driverId"
                                    placeholder={t('reservations.list.driver')}
                                    options={driversOptions}
                                    rules={{ required: isFormEditable ? t('common.errors.required') as string : false }}
                                    isClearable={isFormEditable}
                                    isDisabled={!isFormEditable}
                                    filterOption={(candidate: any, input: any) => input ? removeAccents(candidate.label).toUpperCase().includes(removeAccents(input).toUpperCase()) : true}
                                    onChangeSelect={(data: SelectValueLabel) => {
                                        requestForm.setValue('driverId', data ? data.value : null);
                                    }}
                                />
                                <InputError error={errors.driverId} />
                            </FormItem>
                        </Col>
                    </Row>
                    <Row>
                        <Col sm={12} md={4}>
                            <FormItem>
                                <Label className={styles.label}>{t('vehicle.local' as any)}</Label>
                                <SelectController
                                    form={requestForm}
                                    name="localId"
                                    placeholder={t('vehicle.local' as any)}
                                    options={localsOptions}
                                    isClearable={isFormEditable}
                                    isDisabled={!isFormEditable}
                                    filterOption={(candidate: any, input: any) => input ? removeAccents(candidate.label).toUpperCase().includes(removeAccents(input).toUpperCase()) : true}
                                    onChangeSelect={(data: SelectValueLabel) => {
                                        requestForm.setValue('localId', data ? data.value : null);
                                    }}
                                />
                            </FormItem>
                        </Col>
                        <Col sm={12} md={8}>
                            <FormItem>
                                <Label className={styles.label} required={isFormEditable}>{t('reservations.destination_reason')}</Label>
                                <InputResizableController
                                    name="reason"
                                    form={requestForm as any}
                                    rows={2}
                                    autoComplete='off'
                                    placeholder={t('reservations.destination_reason')}
                                    rules={{ required: isFormEditable ? t('common.errors.required') as string : false, maxLength: 500 }}
                                    disabled={!isFormEditable}
                                />
                                <InputError error={errors.reason} />
                            </FormItem>
                        </Col>
                    </Row>
                </form>

                {/* Vehicle info for details mode (read-only when not editing or vehicle selection not required) */}
                {isDetailsMode && !isApproverOnPending && !(isEditing && requiresVehicleSelection) && vehicleInfo && (
                    <div className={styles.vehicleSection}>
                        <Label className={styles.label}>{t('reservations.list.vehicle')}</Label>
                        <div className={styles.vehicleCell}>
                            <div className={styles.imageCell}>
                                <div className={styles.contentImage} style={{ width: '5rem', height: '3rem' }}>
                                    <div className={styles.img} style={{
                                        width: '5rem', height: '3rem',
                                        backgroundImage: vehicleInfo.photoUrl
                                            ? 'url(' + vehicleInfo.photoUrl + '?_=' + imageCacheKey.current + ')'
                                            : 'url(' + DefaultAssetImage + ')'
                                    }} />
                                </div>
                            </div>
                            <div className={styles.description}>
                                <div className={styles.bold}>{vehicleInfo.regNumber}</div>
                                {(vehicleInfo.brandName || vehicleInfo.modelName) && (
                                    <div className={styles.brandModel}>
                                        {vehicleInfo.brandName}{vehicleInfo.brandName && vehicleInfo.modelName ? ' | ' : ''}{vehicleInfo.modelName}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Select Vehicle section - for create mode, approver on pending, or user editing with vehicle selection */}
                {((!isDetailsMode && requiresVehicleSelection) || isApproverOnPending || (isEditing && requiresVehicleSelection)) && (
                    <>
                        {/* Collapsible filters - gray bar */}
                        <div className={styles.filtersBar} onClick={() => setFiltersOpen(!filtersOpen)}>
                            <span className={styles.toggleIcon}>{filtersOpen ? <FaAngleDown /> : <FaAngleRight />}</span>
                            <span>{t('reservations.search_availability')}</span>
                        </div>

                        {filtersOpen && (
                            <div className={styles.filtersContent}>
                                <form onSubmit={searchForm.handleSubmit(applySearchFilters)}>
                                    <Row>
                                        <Col sm={12} md={4}>
                                            <FormItem>
                                                <Label className={styles.label}>{t('reservations.brand')}</Label>
                                                <SelectController
                                                    form={searchForm}
                                                    name="vehicleBrandId"
                                                    placeholder={t('reservations.brand')}
                                                    options={brandsOptions}
                                                    isClearable={true}
                                                    filterOption={(candidate: any, input: any) => input ? removeAccents(candidate.label).toUpperCase().includes(removeAccents(input).toUpperCase()) : true}
                                                    onChangeSelect={(data: SelectValueLabel) => {
                                                        searchForm.setValue('vehicleBrandId', data ? data.value : null);
                                                        if (!data) {
                                                            searchForm.setValue('vehicleModelId', null);
                                                            setModelsOptions([]);
                                                        } else {
                                                            getModelByBrand();
                                                        }
                                                    }}
                                                />
                                            </FormItem>
                                        </Col>
                                        <Col sm={12} md={4}>
                                            <FormItem>
                                                <Label className={styles.label}>{t('reservations.model')}</Label>
                                                <SelectController
                                                    form={searchForm}
                                                    name="vehicleModelId"
                                                    placeholder={t('reservations.model')}
                                                    options={modelsOptions}
                                                    isClearable={true}
                                                    isDisabled={!searchForm.watch('vehicleBrandId')}
                                                    filterOption={(candidate: any, input: any) => input ? removeAccents(candidate.label).toUpperCase().includes(removeAccents(input).toUpperCase()) : true}
                                                    onChangeSelect={(data: SelectValueLabel) => {
                                                        searchForm.setValue('vehicleModelId', data ? data.value : null);
                                                    }}
                                                />
                                            </FormItem>
                                        </Col>
                                        <Col sm={12} md={4}>
                                            <FormItem>
                                                <Label className={styles.label}>{t('vehicle.type')}</Label>
                                                <SelectController
                                                    form={searchForm}
                                                    name="vehicleSegmentId"
                                                    placeholder={t('vehicle.type')}
                                                    options={segmentsOptions}
                                                    isClearable={true}
                                                    filterOption={(candidate: any, input: any) => input ? removeAccents(candidate.label).toUpperCase().includes(removeAccents(input).toUpperCase()) : true}
                                                    onChangeSelect={(data: SelectValueLabel) => {
                                                        searchForm.setValue('vehicleSegmentId', data ? data.value : null);
                                                    }}
                                                />
                                            </FormItem>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col sm={12} md={4}>
                                            <FormItem>
                                                <Label className={styles.label}>{t('reservations.registration_number')}</Label>
                                                <Input
                                                    maxLength={255}
                                                    name="vehicleRegistrationNumber"
                                                    onKeyUp={e => {
                                                        searchForm.setValue('vehicleRegistrationNumber', e.currentTarget.value !== '' ? e.currentTarget.value : null);
                                                    }}
                                                    placeholder={t('reservations.registration_number')}
                                                    autoComplete='off'
                                                    defaultValue={searchForm.watch('vehicleRegistrationNumber') || undefined}
                                                />
                                            </FormItem>
                                        </Col>
                                        <Col sm={12} md={4}>
                                            <FormItem>
                                                <Label className={styles.label}>{t('reservations.list.date_start')}</Label>
                                                <DateWithHourController
                                                    form={searchForm}
                                                    datePlaceholder={t('common.date')}
                                                    dateFormat={DATE_FORMAT_DEFAUT}
                                                    dateCustomInput={<InputGroup icon={<FaCalendarAlt />} removeIconOrTextBorderRadius={true} />}
                                                    name="reservationStartDate"
                                                    isDisabled={true}
                                                    timePlaceholder={t('common.hour')}
                                                />
                                            </FormItem>
                                        </Col>
                                        <Col sm={12} md={4}>
                                            <FormItem>
                                                <Label className={styles.label}>{t('reservations.list.date_end')}</Label>
                                                <DateWithHourController
                                                    form={searchForm}
                                                    datePlaceholder={t('common.date')}
                                                    dateFormat={DATE_FORMAT_DEFAUT}
                                                    dateCustomInput={<InputGroup icon={<FaCalendarAlt />} removeIconOrTextBorderRadius={true} />}
                                                    name="reservationEndDate"
                                                    isDisabled={true}
                                                    timePlaceholder={t('common.hour')}
                                                    minDate={searchForm.watch('reservationStartDate') || undefined}
                                                />
                                            </FormItem>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col sm={12} md={4}>
                                            <FormItem>
                                                <Label className={styles.label}>{t('vehicle.local' as any)}</Label>
                                                <SelectController
                                                    form={searchForm}
                                                    name="localId"
                                                    placeholder={t('vehicle.local' as any)}
                                                    options={searchLocalsOptions}
                                                    isClearable={false}
                                                    isDisabled={true}
                                                    filterOption={(candidate: any, input: any) => input ? removeAccents(candidate.label).toUpperCase().includes(removeAccents(input).toUpperCase()) : true}
                                                />
                                            </FormItem>
                                        </Col>
                                        <Col sm={12} md={8} className={styles.searchButton}>
                                            <Button
                                                text={t('common.clear') as string}
                                                size={'small'}
                                                preset={'secondary'}
                                                onClick={clearSearchFilters}
                                                type='reset'
                                            />
                                            <Button
                                                type='submit'
                                                text={t('common.apply') as string}
                                                size={'small'}
                                            />
                                        </Col>
                                    </Row>
                                </form>
                            </div>
                        )}

                        {/* Vehicles table */}
                        <div className={styles.tableContainer}>
                            <DataTable
                                columns={vehicleColumns}
                                rows={vehicles}
                                hiddenColumns={[]}
                                disallowHover={true}
                                disableResize={true}
                                onRowClick={row => toggleRowOpen(row)}
                                onRowId={row => row.id ?? ''}
                                expandableRow={expandableRowConfig}
                                onRenderAction={renderSelectAction}
                                trStyle={(row: ReservationsVehicleListItemDto) => selectedVehicle?.id === row.id ? { backgroundColor: 'rgba(2, 199, 199, 0.03)' } : {}}
                                infiniteScroll={{
                                    isLoading: isLoadingMore,
                                    hasMore: vehicles.length < totalItems,
                                    onLoadMore: loadMore,
                                    hasMorePrevious: false,
                                    isLoadingPrevious: false,
                                    // eslint-disable-next-line @typescript-eslint/no-empty-function
                                    onLoadPrevious: () => { },
                                }}
                            />
                        </div>
                    </>
                )}

                {/* History section - visible in details mode for both reservations and requests */}
                {isDetailsMode && reservation?.id && (
                    <ReservationHistoryTab reservationId={reservation.id} refreshKey={historyRefreshKey} />
                )}

                {/* Trips section - only in details mode when reservation has vehicle and dates */}
                {isDetailsMode && !isEditing && reservation?.id && reservation?.vehicleId && reservation?.startDate && reservation?.endDate && (
                    <ReservationTripsSection
                        vehicleId={reservation.vehicleId}
                        startDate={new Date(reservation.startDate)}
                        endDate={new Date(reservation.endDate)}
                        reservationId={reservation.id}
                    />
                )}

                {/* Action buttons for create mode */}
                {!isDetailsMode && (
                    <div className={styles.buttonsContainer}>
                        <Button
                            type="button"
                            text={t('common.cancel') as string}
                            preset={'secondary'}
                            onClick={() => history.push(backUrl)}
                        />
                        <Button
                            type="submit"
                            form="requestForm"
                            text={t('common.save') as string}
                            isDisabled={isSelectedVehicleImmobilized()}
                        />
                    </div>
                )}

                {/* Action buttons for approver on pending request (matching mobile: Reject + Approve) */}
                {isApproverOnPending && (
                    <div className={styles.buttonsContainerSpaced}>
                        <div className={styles.buttonsGroup}>
                            <Button
                                text={t('common.back') as string}
                                preset={'secondary'}
                                onClick={() => history.push(hasBeenEdited ? backUrlWithRefresh : backUrl)}
                            />
                            {canDeleteRequest && (
                                <Button
                                    text={t('common.remove') as string}
                                    preset={'danger'}
                                    onClick={() => setShowDeleteModal(true)}
                                />
                            )}
                        </div>
                        <div className={styles.buttonsGroup}>
                            <Button
                                text={t('requests.reject') as string}
                                preset={'danger'}
                                onClick={() => setShowRejectModal(true)}
                            />
                            <Button
                                text={t('requests.approve') as string}
                                onClick={onApprove}
                                isDisabled={isSelectedVehicleImmobilized()}
                            />
                        </div>
                    </div>
                )}
                {/* Action buttons for details mode (non-approver, view mode) */}
                {isDetailsMode && !isEditing && !isApproverOnPending && (
                    <div className={styles.buttonsContainer}>
                        <Button
                            text={t('common.back') as string}
                            preset={'secondary'}
                            onClick={() => history.push(hasBeenEdited ? backUrlWithRefresh : backUrl)}
                        />
                        {canDeleteRequest && (
                            <Button
                                text={t('common.remove') as string}
                                preset={'danger'}
                                onClick={() => setShowDeleteModal(true)}
                            />
                        )}
                        {canEditRequest && (
                            <Button
                                text={t('common.edit') as string}
                                onClick={() => setIsEditing(true)}
                            />
                        )}
                        {/* Cancel approval button for the approver who approved (only from Pedidos tab) */}
                        {isRequestMode && reservation?.status === 'APPROVED' && canApprove && reservation.approvedByUserId === userProfile?.id && reservation.approvedByUserId !== reservation.userCreatedId && (
                            <Button
                                text={t('requests.cancel_approval') as string}
                                preset={'danger'}
                                onClick={() => setShowCancelApprovalModal(true)}
                            />
                        )}
                    </div>
                )}
                {/* Action buttons for edit mode (non-approver) */}
                {isDetailsMode && isEditing && !isApproverOnPending && (
                    <div className={styles.buttonsContainer}>
                        <Button
                            type="button"
                            text={t('common.cancel') as string}
                            preset={'secondary'}
                            onClick={onCancelEdit}
                        />
                        <Button
                            type="submit"
                            form="requestForm"
                            text={t('common.save') as string}
                            isDisabled={isSelectedVehicleImmobilized()}
                        />
                    </div>
                )}
            </div>

            {/* Rejection modal */}
            <QuestionYesNo
                isVisible={showRejectModal}
                message={t('requests.reject_confirm')}
                onNo={() => { setShowRejectModal(false); setRejectionReason(''); }}
                onYes={onReject}
            >
                <div style={{ marginTop: '1rem' }}>
                    <Label className={styles.label}>{t('requests.rejection_reason_label')}</Label>
                    <InputResizable
                        rows={2}
                        placeholder={t('requests.rejection_reason_placeholder') as string}
                        value={rejectionReason}
                        onChange={e => setRejectionReason(e.target.value)}
                        autoComplete="off"
                    />
                </div>
            </QuestionYesNo>

            {/* Delete confirmation modal */}
            <QuestionYesNo
                isVisible={showDeleteModal}
                message={t('common.messages.remove_record_with_ident')}
                onNo={() => setShowDeleteModal(false)}
                onYes={onDelete}
            />

            {/* Cancel approval confirmation modal */}
            <QuestionYesNo
                isVisible={showCancelApprovalModal}
                message={t('requests.cancel_approval_confirmation')}
                onNo={() => setShowCancelApprovalModal(false)}
                onYes={onCancelApproval}
            />
        </div>
    );
};

export default NewRequestScreen;
